import type React from "react";
import { useEffect, useState } from "react";
import { useGlobalState } from "senf-shared";
import {
  setOsmBuildingsColor,
  toggleOsmBuildings,
} from "./cesium/cesium-imagery";
import type { ImageryStyle } from "./cesium/cesium-types";
import AttributeDisplay from "./cesium/components/AttributeDisplay";
import SettingsPanel from "./cesium/components/SettingsPanel";

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

interface BimModel {
  assetId: number;
  assetName: string;
  detectedEpsg: string;
  fileSize: string;
  uploadDate: Date;
  styles?: StyleCondition[];
}

interface CesiumControlsProps {
  bimModels: BimModel[];
  onBimModelsChange: (models: BimModel[]) => void;
  imageryStyle: ImageryStyle;
  onImageryStyleChange: (style: ImageryStyle) => void;
  polygonDrawingEnabled?: boolean;
  onPolygonDrawingChange?: (enabled: boolean) => void;
  osmBuildingsEnabled: boolean;
  onOsmBuildingsChange: (enabled: boolean) => void;
  osmBuildingColor: string;
  onOsmBuildingColorChange: (color: string, opacity: number) => void;
  osmBuildingOpacity: number;
}

const CesiumControls: React.FC<CesiumControlsProps> = ({
  bimModels,
  onBimModelsChange,
  imageryStyle,
  onImageryStyleChange,
  polygonDrawingEnabled = false,
  onPolygonDrawingChange,
  osmBuildingsEnabled,
  onOsmBuildingsChange,
  osmBuildingColor,
  onOsmBuildingColorChange,
  osmBuildingOpacity,
}) => {
  const [statefulCesiumMap] = useGlobalState().statefulCesiumMap;
  const viewer = statefulCesiumMap?.viewer;
  const [selectedAttributes, setSelectedAttributes] = useState<{
    [key: string]: any;
  } | null>(null);
  const [isAttributeDisplayVisible, setIsAttributeDisplayVisible] =
    useState(false);

  // Collect all style conditions from all assets
  const getAllStyleConditions = (): StyleCondition[] => {
    return bimModels.flatMap((model) => model.styles || []);
  };

  // Apply initial styles when component mounts or when bimModels change
  useEffect(() => {
    const initialConditions = bimModels.flatMap((model) => model.styles || []);
    if (initialConditions.length > 0) {
      console.log(
        "🎨 CesiumControls: Dispatching initial style conditions",
        initialConditions,
      );

      // Dispatch initial styles with a small delay to ensure Cesium is ready
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("styleConditionsChange", {
            detail: { conditions: initialConditions },
          }),
        );
      }, 100);
    }
  }, [bimModels]);

  const handleDepthTestChange = (enabled: boolean) => {
    window.dispatchEvent(
      new CustomEvent("depthTestChange", { detail: { enabled } }),
    );
  };

  const handleShadowsChange = (enabled: boolean) => {
    window.dispatchEvent(
      new CustomEvent("shadowsChange", { detail: { enabled } }),
    );
  };

  const handlePolygonDrawingChange = (enabled: boolean) => {
    if (onPolygonDrawingChange) {
      onPolygonDrawingChange(enabled);
    }
    // You might also want to dispatch a global event if other parts of the app need to know
    window.dispatchEvent(
      new CustomEvent("polygonDrawingChange", { detail: { enabled } }),
    );
  };

  const handleOsmBuildingsChange = (enabled: boolean) => {
    onOsmBuildingsChange(enabled);
    if (viewer) {
      toggleOsmBuildings(viewer, enabled);
    }
  };

  const handleOsmBuildingColorChange = (color: string, opacity: number) => {
    onOsmBuildingColorChange(color, opacity);
    if (viewer) {
      setOsmBuildingsColor(viewer, color, opacity);
    }
  };

  const findAssetForProperty = (
    property: string,
    attributes: { [key: string]: any },
  ): BimModel | null => {
    // Try to find the asset that contains this property
    // For now, we'll use a simple heuristic but this could be improved
    // by analyzing the property structure or asset metadata

    // Check if we can match by asset name or other identifying features
    const propertyString = property.toLowerCase();

    for (const model of bimModels) {
      // If the property name contains any part of the asset name, assign it to that asset
      if (
        propertyString.includes(model.assetName.toLowerCase()) ||
        model.assetName.toLowerCase().includes(propertyString)
      ) {
        return model;
      }
    }

    // If no specific match found, assign to the first asset
    return bimModels[0] || null;
  };

  const handleStyleConditionsChange = (conditions: StyleCondition[]) => {
    // Distribute conditions to appropriate assets based on property analysis
    const updatedModels: BimModel[] = bimModels.map((model) => ({
      ...model,
      styles: [] as StyleCondition[],
    }));

    // Group conditions by asset
    conditions.forEach((condition) => {
      const targetAsset = findAssetForProperty(condition.property, {});
      if (targetAsset) {
        const modelIndex = updatedModels.findIndex(
          (m) => m.assetId === targetAsset.assetId,
        );
        if (modelIndex >= 0 && updatedModels[modelIndex].styles) {
          updatedModels[modelIndex].styles!.push(condition);
        }
      }
    });

    // If no asset was found for some conditions, put them on the first asset
    const orphanConditions = conditions.filter((condition) => {
      return !updatedModels.some(
        (model) =>
          model.styles &&
          model.styles.some(
            (s) =>
              s.property === condition.property && s.value === condition.value,
          ),
      );
    });

    if (
      orphanConditions.length > 0 &&
      updatedModels.length > 0 &&
      updatedModels[0].styles
    ) {
      updatedModels[0].styles.push(...orphanConditions);
    }

    onBimModelsChange(updatedModels);

    window.dispatchEvent(
      new CustomEvent("styleConditionsChange", {
        detail: { conditions },
      }),
    );
  };

  const handleAttributeSelect = (property: string, value: any) => {
    const newCondition: StyleCondition = {
      property,
      value: String(value),
      color: "#ffffff",
      opacity: 1.0,
    };

    const currentConditions = getAllStyleConditions();
    const conditionExists = currentConditions.some(
      (c) =>
        c.property === newCondition.property && c.value === newCondition.value,
    );

    if (!conditionExists) {
      const updatedConditions = [...currentConditions, newCondition];
      handleStyleConditionsChange(updatedConditions);
    }

    setIsAttributeDisplayVisible(false);
  };

  const handleCloseAttributeDisplay = () => {
    setIsAttributeDisplayVisible(false);
  };

  const handleObjectClick = (attributes: { [key: string]: any }) => {
    setSelectedAttributes(attributes);
    setIsAttributeDisplayVisible(true);
  };

  // Set up event listeners for cesium object clicks
  useEffect(() => {
    const handleCesiumObjectClick = (event: CustomEvent) => {
      handleObjectClick(event.detail.attributes || event.detail);
    };

    const handleCesiumEmptyClick = () => {
      // Close AttributeDisplay when clicking on empty space or non-BIM objects
      setIsAttributeDisplayVisible(false);
    };

    // Listen for various cesium click events
    window.addEventListener(
      "cesiumObjectClick",
      handleCesiumObjectClick as EventListener,
    );
    window.addEventListener(
      "objectClick",
      handleCesiumObjectClick as EventListener,
    );
    window.addEventListener(
      "cesiumEmptyClick",
      handleCesiumEmptyClick as EventListener,
    );

    // Also dispatch a ready event so cesium knows we're listening
    window.dispatchEvent(new CustomEvent("cesiumControlsReady"));

    return () => {
      window.removeEventListener(
        "cesiumObjectClick",
        handleCesiumObjectClick as EventListener,
      );
      window.removeEventListener(
        "objectClick",
        handleCesiumObjectClick as EventListener,
      );
      window.removeEventListener(
        "cesiumEmptyClick",
        handleCesiumEmptyClick as EventListener,
      );
    };
  }, []);

  return (
    <>
      <SettingsPanel
        onDepthTestChange={handleDepthTestChange}
        onShadowsChange={handleShadowsChange}
        onPolygonDrawingChange={handlePolygonDrawingChange}
        imageryStyle={imageryStyle}
        onImageryStyleChange={onImageryStyleChange}
        conditions={getAllStyleConditions()}
        onStyleConditionsChange={handleStyleConditionsChange}
        osmBuildingsEnabled={osmBuildingsEnabled}
        onOsmBuildingsChange={handleOsmBuildingsChange}
        osmBuildingColor={osmBuildingColor}
        onOsmBuildingColorChange={handleOsmBuildingColorChange}
        osmBuildingOpacity={osmBuildingOpacity}
      />
      {selectedAttributes && isAttributeDisplayVisible && (
        <AttributeDisplay
          attributes={selectedAttributes}
          visible={isAttributeDisplayVisible}
          onClose={handleCloseAttributeDisplay}
          onAttributeClick={handleAttributeSelect}
        />
      )}
    </>
  );
};

export default CesiumControls;
